import { useCallback } from 'react';
import { TimelineState, SegmentWithUI, DEFAULT_FPS } from '../types/timeline';
import { TimelineService } from '../services/timelineService';

// === FRAME ARITHMETIC HELPERS ===
const toFrames = (timeSec: number, fps: number): number => Math.round(timeSec * fps);
const toSeconds = (frames: number, fps: number): number => frames / fps;

interface UseTimelineActionsProps {
    timelineState: TimelineState;
    setTimelineState: React.Dispatch<React.SetStateAction<TimelineState>>;
    pushToHistory: () => void;
    setSelectedGap: (gap: { trackId: string; inSec: number; duration: number } | null) => void;
}

export const useTimelineActions = ({
    timelineState,
    setTimelineState,
    pushToHistory,
    setSelectedGap
}: UseTimelineActionsProps) => {

    const handleSegmentReorder = useCallback(async (newSegments: SegmentWithUI[]) => {
        // Optimistic update
        setTimelineState(prev => ({ ...prev, segments: newSegments }));

        const projectId = timelineState.project?.id;
        if (projectId) {
            try {
                const orders = Object.fromEntries(newSegments.map((s, i) => [s.id, i + 1]));
                await TimelineService.reorderSegments(projectId, orders);
            } catch (err) {
                console.error('[Timeline] Reorder failed:', err);
            }
        }
    }, [timelineState.project, setTimelineState]);

    const handleSegmentDelete = useCallback(async (segmentId: string, ripple: boolean = false) => {
        const segment = timelineState.segments.find(s => s.id === segmentId);
        if (!segment) return;

        const fps = timelineState.project?.fps || DEFAULT_FPS;
        const deletedDurationFrames = toFrames(segment.durationSec, fps);
        const deletedEndFrame = toFrames(segment.outSec, fps);
        const trackId = segment.trackId;

        // Find linked segments to also delete
        const linkedIds = segment.linkGroupId
            ? timelineState.segments
                .filter(s => s.linkGroupId === segment.linkGroupId)
                .map(s => s.id)
            : [segmentId];

        pushToHistory();

        setTimelineState(prev => {
            let newSegments = prev.segments.filter(s => !linkedIds.includes(s.id));

            // RIPPLE: Shift following segments on same track(s) left
            if (ripple) {
                const tracksToShift = segment.linkGroupId
                    ? ['v1', 'a1'] // Shift both linked tracks
                    : [trackId];

                newSegments = newSegments.map(seg => {
                    const segInFrame = toFrames(seg.inSec, fps);
                    if (tracksToShift.includes(seg.trackId) && segInFrame >= deletedEndFrame) {
                        const newInFrame = segInFrame - deletedDurationFrames;
                        const newOutFrame = toFrames(seg.outSec, fps) - deletedDurationFrames;
                        const newStartFrame = (seg.startFrame ?? segInFrame) - deletedDurationFrames; // Shift absolute frame

                        return {
                            ...seg,
                            inSec: toSeconds(newInFrame, fps),
                            outSec: toSeconds(newOutFrame, fps),
                            startFrame: newStartFrame
                        };
                    }
                    return seg;
                });
                console.log(`[RIPPLE DELETE] Shifted segments left by ${deletedDurationFrames} frames`);
            }

            return {
                ...prev,
                segments: newSegments,
                selectedSegmentIds: prev.selectedSegmentIds.filter(id => !linkedIds.includes(id))
            };
        });

        // Persist to backend
        try {
            for (const id of linkedIds) {
                await TimelineService.deleteSegment(id);
            }
        } catch (err) {
            console.error('[Timeline] Delete failed:', err);
        }
    }, [timelineState.segments, timelineState.project, setTimelineState, pushToHistory]);

    const handleDeleteGap = useCallback((atSec: number, trackId: string) => {
        pushToHistory();
        setTimelineState(s => {
            const fps = s.project?.fps || DEFAULT_FPS;
            const atFrame = toFrames(atSec, fps);

            const trackSegments = s.segments
                .filter(seg => seg.trackId === trackId)
                .sort((a, b) => a.inSec - b.inSec);

            let gapStartFrame = 0;
            let gapEndFrame = atFrame;

            // Find gap boundaries in frames
            for (const seg of trackSegments) {
                const segInFrame = toFrames(seg.inSec, fps);
                const segOutFrame = toFrames(seg.outSec, fps);

                if (segInFrame > gapStartFrame && segInFrame <= atFrame) {
                    gapStartFrame = segOutFrame;
                }
                if (segInFrame > atFrame) {
                    gapEndFrame = segInFrame;
                    break;
                }
            }
            const gapDurationFrames = gapEndFrame - gapStartFrame;

            if (gapDurationFrames > 1) { // Tolerance > 1 frame
                return {
                    ...s,
                    segments: s.segments.map(seg => {
                        const segInFrame = toFrames(seg.inSec, fps);
                        if (seg.trackId === trackId && segInFrame >= gapEndFrame) {
                            const newInFrame = segInFrame - gapDurationFrames;
                            const newOutFrame = toFrames(seg.outSec, fps) - gapDurationFrames;
                            const newStartFrame = (seg.startFrame ?? segInFrame) - gapDurationFrames;

                            return {
                                ...seg,
                                inSec: toSeconds(newInFrame, fps),
                                outSec: toSeconds(newOutFrame, fps),
                                startFrame: newStartFrame
                            };
                        }
                        return seg;
                    })
                };
            }
            return s;
        });
        setSelectedGap(null);
    }, [pushToHistory, setTimelineState, setSelectedGap]);

    const handleSegmentDuplicate = useCallback(async (segmentId: string) => {
        const segment = timelineState.segments.find(s => s.id === segmentId);
        if (!segment) return;

        pushToHistory();

        try {
            const newSegment = await TimelineService.duplicateSegment(segment);
            const duplicateWithUI: SegmentWithUI = { ...newSegment, uiState: 'idle' };

            setTimelineState(prev => {
                const segmentIndex = prev.segments.findIndex(s => s.id === segmentId);
                if (segmentIndex === -1) return prev;
                const newSegments = [...prev.segments];
                newSegments.splice(segmentIndex + 1, 0, duplicateWithUI);
                return { ...prev, segments: newSegments };
            });
        } catch (err) {
            console.error('[Timeline] Duplicate failed:', err);
            // Fallback local duplicate
            setTimelineState(prev => {
                const segmentIndex = prev.segments.findIndex(s => s.id === segmentId);
                if (segmentIndex === -1) return prev;
                const original = prev.segments[segmentIndex];
                const duplicate: SegmentWithUI = {
                    ...original,
                    id: crypto.randomUUID(),
                    label: `${original.label || 'Segment'} (Copy)`,
                    createdAt: new Date().toISOString(),
                    uiState: 'idle'
                };
                const newSegments = [...prev.segments];
                newSegments.splice(segmentIndex + 1, 0, duplicate);
                return { ...prev, segments: newSegments };
            });
        }
    }, [timelineState.segments, setTimelineState, pushToHistory]);

    const handleSplitSegment = useCallback((segmentId: string, atSec: number) => {
        pushToHistory();

        setTimelineState(prev => {
            const segmentIndex = prev.segments.findIndex(s => s.id === segmentId);
            if (segmentIndex === -1) return prev;

            const original = prev.segments[segmentIndex];
            const fps = prev.project?.fps || DEFAULT_FPS;

            // Frame Arithmetic
            const splitFrame = toFrames(atSec, fps);
            const originalInFrame = toFrames(original.inSec, fps);
            const originalOutFrame = toFrames(original.outSec, fps);

            // Bounds check
            if (splitFrame <= originalInFrame || splitFrame >= originalOutFrame) return prev;

            // Calculate durations in frames
            const leftDurationFrames = splitFrame - originalInFrame;
            // rightDurationFrames = originalOutFrame - splitFrame; 

            // Source math (frames)
            const originalSourceStartFrame = original.sourceStartFrame ?? toFrames(original.sourceInSec ?? 0, fps);
            // We trust sourceStartFrame as source of truth.
            // If it's missing, we infer from sourceInSec, but prefer the frame field if set.

            const rightSourceStartFrame = originalSourceStartFrame + leftDurationFrames;

            // Reconstruct seconds for UI/DB
            const splitSec = toSeconds(splitFrame, fps);

            // LEFT SEGMENT
            const leftSegment: SegmentWithUI = {
                ...original,
                id: `${original.id}_left_${Date.now()}`,

                // Timeline Position
                inSec: original.inSec, // unchanged
                outSec: splitSec,
                durationSec: toSeconds(leftDurationFrames, fps),
                startFrame: original.startFrame ?? originalInFrame, // unchanged start
                durationFrames: leftDurationFrames,

                // Source Mapping
                sourceStartFrame: originalSourceStartFrame,
                sourceDurationFrames: leftDurationFrames,
                sourceInSec: original.sourceInSec ?? 0,
                sourceOutSec: toSeconds(originalSourceStartFrame + leftDurationFrames, fps),

                label: `${original.label || 'Segment'} (1)`,
                updatedAt: new Date().toISOString()
            };

            // RIGHT SEGMENT
            const rightDurationFrames = originalOutFrame - splitFrame;

            const rightSegment: SegmentWithUI = {
                ...original,
                id: `${original.id}_right_${Date.now()}`,

                // Timeline Position
                inSec: splitSec,
                outSec: original.outSec, // unchanged end
                durationSec: toSeconds(rightDurationFrames, fps),
                startFrame: splitFrame, // new start
                durationFrames: rightDurationFrames,

                // Source Mapping
                sourceStartFrame: rightSourceStartFrame,
                sourceDurationFrames: rightDurationFrames,
                sourceInSec: toSeconds(rightSourceStartFrame, fps),
                sourceOutSec: original.sourceOutSec ?? toSeconds(rightSourceStartFrame + rightDurationFrames, fps),

                label: `${original.label || 'Segment'} (2)`,
                order: original.order + 1,
                updatedAt: new Date().toISOString()
            };

            const newSegmentsToInsert: SegmentWithUI[] = [leftSegment, rightSegment];
            const segmentIdsToRemove = new Set<string>([original.id]);

            // Handle Linked Segments (Ripple Split)
            if (original.linkGroupId) {
                const linkedSegments = prev.segments.filter(
                    s => s.linkGroupId === original.linkGroupId && s.id !== original.id
                );

                for (const linked of linkedSegments) {
                    const linkedInFrame = toFrames(linked.inSec, fps);
                    const linkedOutFrame = toFrames(linked.outSec, fps);

                    if (splitFrame > linkedInFrame && splitFrame < linkedOutFrame) {
                        segmentIdsToRemove.add(linked.id);

                        const linkedSplitRelFrames = splitFrame - linkedInFrame;
                        const linkedSourceStartFrame = linked.sourceStartFrame ?? toFrames(linked.sourceInSec ?? 0, fps);

                        const linkedLeft: SegmentWithUI = {
                            ...linked,
                            id: `${linked.id}_left_${Date.now()}`,
                            outSec: splitSec,
                            durationSec: toSeconds(linkedSplitRelFrames, fps),
                            durationFrames: linkedSplitRelFrames,
                            sourceDurationFrames: linkedSplitRelFrames,
                            sourceOutSec: toSeconds(linkedSourceStartFrame + linkedSplitRelFrames, fps),
                            label: `${linked.label || 'Segment'} (1)`,
                            updatedAt: new Date().toISOString()
                        };

                        const linkedRightDurationFrames = linkedOutFrame - splitFrame;
                        const linkedRightSourceStartFrame = linkedSourceStartFrame + linkedSplitRelFrames;

                        const linkedRight: SegmentWithUI = {
                            ...linked,
                            id: `${linked.id}_right_${Date.now()}`,
                            inSec: splitSec,
                            startFrame: splitFrame, // new absolute start
                            durationSec: toSeconds(linkedRightDurationFrames, fps),
                            durationFrames: linkedRightDurationFrames,

                            sourceStartFrame: linkedRightSourceStartFrame,
                            sourceDurationFrames: linkedRightDurationFrames,
                            sourceInSec: toSeconds(linkedRightSourceStartFrame, fps),

                            label: `${linked.label || 'Segment'} (2)`,
                            order: linked.order + 1,
                            updatedAt: new Date().toISOString()
                        };
                        newSegmentsToInsert.push(linkedLeft, linkedRight);
                    }
                }
            }

            const filteredSegments = prev.segments.filter(s => !segmentIdsToRemove.has(s.id));
            const finalSegments = [...filteredSegments, ...newSegmentsToInsert];

            const sortedSegments = finalSegments.sort((a, b) => {
                if (a.trackId !== b.trackId) return a.trackId.localeCompare(b.trackId);
                return a.inSec - b.inSec;
            });

            return {
                ...prev,
                segments: sortedSegments.map((s, i) => ({ ...s, order: i })),
                selectedSegmentIds: [rightSegment.id]
            };
        });
    }, [setTimelineState, pushToHistory]);

    const handleSegmentTrim = useCallback((segmentId: string, edge: 'start' | 'end', newTime: number) => {
        pushToHistory();
        setTimelineState(s => {
            const fps = s.project?.fps || DEFAULT_FPS;

            // Snap requested time to frame grid
            const targetFrame = toFrames(newTime, fps);

            return {
                ...s,
                segments: s.segments.map(seg => {
                    if (seg.id !== segmentId) return seg;

                    const segInFrame = toFrames(seg.inSec, fps);
                    const segOutFrame = toFrames(seg.outSec, fps);
                    const segSourceStartFrame = seg.sourceStartFrame ?? toFrames(seg.sourceInSec ?? 0, fps);

                    if (edge === 'start') {
                        // Trimming HEAD: Move IN point, adjust duration and source start
                        // targetFrame cannot be >= outFrame
                        const clampedInFrame = Math.min(targetFrame, segOutFrame - 1);
                        const deltaFrames = clampedInFrame - segInFrame;

                        const newDurationFrames = segOutFrame - clampedInFrame;
                        const newSourceStartFrame = segSourceStartFrame + deltaFrames;

                        return {
                            ...seg,
                            inSec: toSeconds(clampedInFrame, fps),
                            durationSec: toSeconds(newDurationFrames, fps),
                            startFrame: clampedInFrame,
                            durationFrames: newDurationFrames,

                            sourceStartFrame: newSourceStartFrame,
                            sourceDurationFrames: newDurationFrames,
                            sourceInSec: toSeconds(newSourceStartFrame, fps)
                        };
                    } else {
                        // Trimming TAIL: Move OUT point, adjust duration
                        // targetFrame must be > inFrame
                        const clampedOutFrame = Math.max(targetFrame, segInFrame + 1);
                        const newDurationFrames = clampedOutFrame - segInFrame;

                        return {
                            ...seg,
                            outSec: toSeconds(clampedOutFrame, fps),
                            durationSec: toSeconds(newDurationFrames, fps),

                            durationFrames: newDurationFrames,
                            sourceDurationFrames: newDurationFrames,
                            sourceOutSec: toSeconds(segSourceStartFrame + newDurationFrames, fps)
                        };
                    }
                })
            };
        });
    }, [setTimelineState, pushToHistory]);

    const handleSegmentMove = useCallback((segmentId: string, newInSec: number) => {
        pushToHistory();
        setTimelineState(s => {
            const fps = s.project?.fps || DEFAULT_FPS;
            // Snap to frame
            const newInFrame = toFrames(newInSec, fps);

            return {
                ...s,
                segments: s.segments.map(seg => {
                    if (seg.id !== segmentId) return seg;

                    const durationFrames = seg.durationFrames ?? toFrames(seg.durationSec, fps);
                    const newOutFrame = newInFrame + durationFrames;

                    return {
                        ...seg,
                        inSec: toSeconds(newInFrame, fps),
                        outSec: toSeconds(newOutFrame, fps),
                        startFrame: newInFrame, // Absolute timeline pos
                        // Note: Source mapping doesn't change on move
                    };
                })
            };
        });
    }, [setTimelineState, pushToHistory]);


    return {
        handleSegmentReorder,
        handleSegmentDelete,
        handleDeleteGap,
        handleSegmentDuplicate,
        handleSplitSegment,
        handleSegmentTrim,
        handleSegmentMove
    };
};
